import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { apiurl } from "../config/config";

/* ================= FLOOR CONFIG ================= */

const FLOOR_HEADS = [
  {
    key: "GMIT",
    name: "Rahul Bhusan",
    target: 1200,
    photo: "/managers/rahulbhusan.jpeg",
  },
  {
    key: "ALTF",
    name: "Anand Suman",
    target: 2000,
    photo: "/managers/anandsuman.png",
  },
];

const FLOOR_MANAGERS = {
  GMIT: ["JayVijay5099", "LavitBhati714"],
  ALTF: ["SanjayRewani1619", "ASHWANI3434", "Himanshu1203", "Abhishek4629"],
};

/* ================= MANAGER META ================= */

const MANAGER_META = {
  SanjayRewani1619: {
    name: "Sanjay",
    target: 800,
    photo: "/managers/sanjayrewani.jpeg",
  },
  Abhishek4629: {
    name: "Abhishek",
    target: 300,
    photo: "/managers/abhishek.jpeg",
  },
  Himanshu1203: {
    name: "Himanshu",
    target: 300,
    photo: "/managers/himanshu.png",
  },
  ASHWANI3434: { name: "Ashwani", target: 300, photo: "/managers/ashwani.png" },
    LavitBhati714: { name: "Lavit", target: 400, photo: "/managers/lavit.jpeg" },
  JayVijay5099: { name: "JayVijay", target: 800, photo: "/managers/jay.jpeg" },
};

/* ================= SLIDES ================= */

const SLIDES = [
  { type: "FLOOR" },
  { type: "MANAGER", A: "JayVijay5099", B: "SanjayRewani1619" },
  { type: "MANAGER", A: "Himanshu1203", B: "ASHWANI3434" },
  { type: "MANAGER", A: "LavitBhati714", B: "Abhishek4629" },
];

const winnerOf = (a, b) => (a === b ? "DRAW" : a > b ? "A" : "B");

const getColor = (p) => {
  if (p >= 100) return "#16a34a";
  if (p >= 90) return "#22c55e";
  if (p >= 80) return "#4ade80";
  if (p >= 70) return "#a3e635";
  if (p >= 60) return "#facc15";
  if (p >= 50) return "#fbbf24";
  if (p >= 40) return "#fb923c";
  if (p >= 30) return "#f97316";
  if (p >= 20) return "#ef4444";
  return "#dc2626";
};

const getProgressMeta = (p) => {
  if (p >= 100) return { color: "#16a34a", text: "TARGET ACHIEVED" };
  if (p >= 90) return { color: "#22c55e", text: "EXCELLENT" };
  if (p >= 80) return { color: "#4ade80", text: "VERY GOOD" };
  if (p >= 70) return { color: "#a3e635", text: "ON TRACK" };
  if (p >= 60) return { color: "#facc15", text: "NEEDS PUSH" };
  if (p >= 50) return { color: "#fbbf24", text: "NEEDS PUSH" };
  if (p >= 40) return { color: "#fb923c", text: "CRITICAL PUSH" };
  if (p >= 30) return { color: "#f97316", text: "CRITICAL PUSH" };
  if (p >= 20) return { color: "#ef4444", text: "ALERT" };
  return { color: "#dc2626", text: "HIGH ALERT" };
};

const getBattleCommentary = (A, B, aVal, bVal) => {
  if (aVal === bVal) {
    return {
      headline: "It's a Perfect Draw 🤝",
      sub: "Anyone can take the lead with 1 order!",
    };
  }

  const leaderId = aVal > bVal ? A : B;
  const trailerId = aVal > bVal ? B : A;
  const diff = Math.abs(aVal - bVal);

  return {
    headline: `${MANAGER_META[leaderId].name} is leading by +${diff} orders`,
    sub: `${MANAGER_META[trailerId].name} needs ${diff + 1} more orders to WIN 🏆`,
  };
};

/* ================= PAGE ================= */

export default function ManagerFloorDisplay() {
  const [managerOrders, setManagerOrders] = useState({});
  const [slide, setSlide] = useState(0);
  const [lastWinner, setLastWinner] = useState(null);

  /* ---------- API ---------- */
  useEffect(() => {
    const fetchData = async () => {
      const res = await axios.get(`${apiurl}/utils/crm/realTimeOrderData`, {
        headers: { token: "ad2355a9d54335e27e2c3a3ed5b6e381" },
        params: { from_date: "2026-01-27", to_date: "2026-01-27" },
      });

      const list = res?.data?.data?.result?.data?.managers || [];
      const map = {};
      list.forEach((m) => (map[m.o_manager_name] = Number(m.totalOrders || 0)));
      setManagerOrders(map);
    };

    fetchData();
    const api = setInterval(fetchData, 30000);
    return () => clearInterval(api);
  }, []);

  /* ---------- AUTO SLIDE ---------- */
  useEffect(() => {
    const swipe = setInterval(() => {
      setSlide((s) => (s + 1) % SLIDES.length);
    }, 10000);
    return () => clearInterval(swipe);
  }, []);

  const current = SLIDES[slide];

  /* ---------- FLOOR VALUES ---------- */
  const floorData = useMemo(() => {
    const sum = (arr) => arr.reduce((a, b) => a + (managerOrders[b] || 0), 0);
    return {
      GMIT: sum(FLOOR_MANAGERS.GMIT),
      ALTF: sum(FLOOR_MANAGERS.ALTF),
    };
  }, [managerOrders]);

  /* ---------- MANAGER VALUES ---------- */
  const managerBattle = useMemo(() => {
    if (current.type !== "MANAGER") return null;
    const aVal = managerOrders[current.A] || 0;
    const bVal = managerOrders[current.B] || 0;
    return {
      aVal,
      bVal,
      win: winnerOf(aVal, bVal),
      diff: Math.abs(aVal - bVal),
    };
  }, [current, managerOrders]);

  /* ---------- CONFETTI (SAFE) ---------- */
  useEffect(() => {
    if (managerBattle?.win && managerBattle.win !== "DRAW") {
      confetti({ particleCount: 120, spread: 100 });
      setLastWinner(managerBattle.win);
    }
  }, [managerBattle]);

  /* ================= RENDER ================= */

  if (current.type === "FLOOR") {
    const win = winnerOf(floorData.GMIT, floorData.ALTF);

    return (
      <SlideWrapper>
        <h1 className="text-6xl font-black text-center mb-10">
          Live Floor Performance
        </h1>
        <div className="grid grid-cols-2 gap-12">
          {/* <FloorCard data={FLOOR_HEADS[0]} value={floorData.GMIT} status={win === "A" ? "WIN" : win === "DRAW" ? "DRAW" : "LOSE"} />
          <FloorCard data={FLOOR_HEADS[1]} value={floorData.ALTF} status={win === "B" ? "WIN" : win === "DRAW" ? "DRAW" : "LOSE"} /> */}

          <AdvancedFloorCard
            floor={FLOOR_HEADS[0]}
            value={floorData.GMIT}
            achievement={Math.round(
              (floorData.GMIT / FLOOR_HEADS[0].target) * 100,
            )}
            isWinner={win === "A"}
            isLoser={win === "B"}
            isDraw={win === "DRAW"}
          />

          <AdvancedFloorCard
            floor={FLOOR_HEADS[1]}
            value={floorData.ALTF}
            achievement={Math.round(
              (floorData.ALTF / FLOOR_HEADS[1].target) * 100,
            )}
            isWinner={win === "B"}
            isLoser={win === "A"}
            isDraw={win === "DRAW"}
          />
        </div>
      </SlideWrapper>
    );
  }

  const { A, B } = current;
  const { aVal, bVal, win, diff } = managerBattle;

  const commentary = getBattleCommentary(A, B, aVal, bVal);

  return (
    <SlideWrapper>
      {/* <motion.h1
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ repeat: Infinity, duration: 1.4 }}
        className="text-5xl font-black text-center mb-8"
      >
        {win === "DRAW"
          ? "Perfect Draw 🤝"
          : `${MANAGER_META[win === "A" ? A : B].name} leads by ${diff} orders`}
      </motion.h1> */}

      {/* Live Lead Banner */}
      {/* <div className="flex justify-center">
            <LiveLeadBanner
              text={win === "DRAW"
                ? "It's a Draw!"
                : `${MANAGER_META[win === "A" ? A : B].name} is Leading!`}
              winner={win === "DRAW" ? null : win === "A" ? A : B}
              // diff={Math.abs(orders.A - orders.B)}
            />
          </div> */}

      <LiveCommentary headline={commentary.headline} sub={commentary.sub} />

      {/* <div className="grid grid-cols-2 gap-12">
        <ManagerCard id={A} value={aVal} status={win === "A" ? "WIN" : win === "DRAW" ? "DRAW" : "LOSE"} />
        <ManagerCard id={B} value={bVal} status={win === "B" ? "WIN" : win === "DRAW" ? "DRAW" : "LOSE"} />
      </div> */}

      {/* <div className="relative grid grid-cols-3 items-center gap-10">
  <ManagerCard
    id={A}
    value={aVal}
    status={win === "A" ? "WIN" : win === "DRAW" ? "DRAW" : "LOSE"}
  />


  <VSBadge win={win} />

  <ManagerCard
    id={B}
    value={bVal}
    status={win === "B" ? "WIN" : win === "DRAW" ? "DRAW" : "LOSE"}
  />
</div> */}

      {/* <div className="grid grid-cols-[45%_10%_45%] items-center gap-3"> */}
      <div className="grid grid-cols-2 items-center gap-3">
        <ManagerCard
          id={A}
          value={aVal}
          status={win === "A" ? "WIN" : win === "DRAW" ? "DRAW" : "LOSE"}
          big
        />

        {/* <VSCard /> */}

        <ManagerCard
          id={B}
          value={bVal}
          status={win === "B" ? "WIN" : win === "DRAW" ? "DRAW" : "LOSE"}
          big
        />
      </div>
    </SlideWrapper>
  );
}

/* ================= UI ================= */

const SlideWrapper = ({ children }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5 mt-10">
    <AnimatePresence mode="wait">
      <motion.div
        key={Math.random()}
        initial={{ opacity: 0, x: 80 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -80 }}
        transition={{ duration: 0.6 }}
        className="w-full bg-white rounded-3xl shadow-2xl p-5 border border-black m-10"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  </div>
);

const FloorCard = ({ data, value, status }) => {
  const pct = Math.min(100, Math.round((value / data.target) * 100));
  const color = getColor(pct);

  return (
    <motion.div
      animate={status === "WIN" ? { scale: [1, 1.03, 1] } : {}}
      transition={{ repeat: Infinity, duration: 1.5 }}
      className={`relative p-8 rounded-3xl border-4 text-center ${
        status === "WIN"
          ? "shadow-[0_0_50px_rgba(34,197,94,0.5)]"
          : status === "LOSE"
            ? //   ? "opacity-80 grayscale"
              " "
            : ""
      }`}
      style={{ borderColor: color }}
    >
      <StatusStrip status={status} />

      <div
        className="mx-auto w-64 h-64 rounded-full p-2 mb-6"
        style={{
          background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        }}
      >
        <img
          src={data.photo}
          className="w-full h-full rounded-full border-4 mt-3"
          style={{ borderColor: color }}
        />
      </div>

      <h2 className="text-4xl font-black">{data.key}</h2>
      <p className="text-xl">{data.name}</p>

      <p className="text-6xl font-black mt-4" style={{ color }}>
        {value}
      </p>

      <p className="text-lg text-gray-600 mb-3">
        Target: <span className="font-bold">{data.target}</span>
      </p>

      <div className="h-5 bg-gray-200 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1 }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>

      <p className="mt-2 text-xl font-bold" style={{ color }}>
        {pct}% achieved
      </p>
    </motion.div>
  );
};

const ManagerCard = ({ id, value, status }) => {
  const m = MANAGER_META[id];
  const pct = Math.min(100, Math.round((value / m.target) * 100));

  const target = Number(m.target);
  const remaining = Math.max(target - value, 0);

  // const color = getColor(pct);

  const { color, text } = getProgressMeta(pct);

  return (
    <motion.div
      animate={status === "WIN" ? { scale: [1, 1.03, 1] } : {}}
      transition={{ repeat: Infinity, duration: 1.4 }}
      className={`relative p-8 rounded-3xl border-4 text-center overflow-hidden ${
        status === "WIN"
          ? "shadow-[0_0_40px_rgba(34,197,94,0.6)]"
          : status === "LOSE"
            ? //   ? "opacity-80 grayscale"
              " "
            : ""
      }`}
      // style={{ borderColor: color }}
      style={{ borderColor: color }}
    >
      <StatusStrip status={status} />

      {/* IMAGE */}
      <div
        className="mx-auto w-72 h-72 rounded-full p-2 mb-6"
        style={{
          background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        }}
      >
        <img
          src={m.photo}
          className="w-full h-full rounded-full border-4 mt-3"
          style={{ borderColor: color }}
        />
      </div>

      {/* NAME */}
      <h2 className="text-3xl font-black">{m.name}</h2>

      {/* VALUE */}
      {/* <p className="text-6xl font-black my-3">
        <span style={{ color }}>{value}</span>/
        <span className="font-black">{m.target}</span>
      </p> */}

      <div className="text-[96px] font-extrabold leading-none">
        <span style={{ color: color }}>{value}</span>
        <span className="opacity-40 text-[56px]"> / {target}</span>
      </div>

      <p className="mt-4 text-[34px] font-semibold text-gray-700">
        {remaining > 0
          ? `${remaining} orders to target`
          : "Target completed 🎉"}
      </p>

      {/* PROGRESS BAR */}
      <div className="h-5 bg-gray-200 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1 }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>

      <div className="mt-4 flex justify-between items-center">
        <p className="text-[36px] font-extrabold" style={{ color }}>
          {pct}% achieved
        </p>

        <p className="text-[40px] font-black tracking-wider" style={{ color }}>
          {text}
        </p>
      </div>
    </motion.div>
  );
};

const StatusStrip = ({ status }) => (
  <div
    className={`absolute top-0 left-0 right-0 py-2 text-white font-bold text-xl rounded-t-3xl ${
      status === "WIN"
        ? "bg-green-600"
        : status === "LOSE"
          ? "bg-red-600"
          : "bg-gray-600"
    }`}
  >
    {status === "WIN"
      ? "🏆 WINNER"
      : status === "LOSE"
        ? "😞 LOSER"
        : "🤝 DRAW"}
  </div>
);

function LiveLeadBanner({ text, winner }) {
  return (
    <motion.div
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`relative w-full max-w-4xl mx-auto mt-6
        rounded-3xl overflow-hidden shadow-2xl
        ${
          winner
            ? "bg-gradient-to-r from-green-500 via-emerald-500 to-green-600"
            : "bg-gradient-to-r from-gray-600 to-gray-800"
        }
      `}
    >
      {/* moving shine */}
      <motion.div
        className="absolute inset-0 bg-white/20"
        initial={{ x: "-100%" }}
        animate={{ x: "200%" }}
        transition={{ repeat: Infinity, duration: 2.5, ease: "linear" }}
      />

      <div className="relative z-10 flex items-center justify-between px-10 py-5 text-white">
        <div className="flex items-center gap-4">
          <motion.span
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="text-4xl"
          >
            🏆
          </motion.span>

          <span className="text-3xl font-extrabold tracking-wide">
            {winner ? text : "MATCH LEVELLED"}
          </span>
        </div>

        <motion.span
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ repeat: Infinity, duration: 1 }}
          className="bg-black/40 px-4 py-2 rounded-full text-lg font-bold"
        >
          LIVE
        </motion.span>
      </div>
    </motion.div>
  );
}

const VSBadge = ({ win }) => (
  <motion.div
    initial={{ scale: 0.7, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.5 }}
    className={`flex items-center justify-center
      w-40 h-40 rounded-full text-5xl font-black
      ${
        win === "DRAW"
          ? "bg-gray-700 text-white"
          : "bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 text-white"
      }
      shadow-[0_0_60px_rgba(255,165,0,0.6)]`}
  >
    VS
  </motion.div>
);

function LiveCommentary({ headline, sub }) {
  return (
    <motion.div
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="mb-10 w-full mx-auto rounded-3xl
                 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600
                 text-white shadow-[0_0_60px_rgba(99,102,241,0.8)]"
    >
      <div className="px-12 py-6 text-center">
        <motion.h2
          animate={{ scale: [1, 1.04, 1] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="text-5xl font-black tracking-wide"
        >
          {headline}
        </motion.h2>

        <p className="mt-3 text-3xl font-bold text-yellow-300">{sub}</p>

        <motion.span
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ repeat: Infinity, duration: 1 }}
          className="inline-block mt-4 bg-black/40 px-6 py-2 rounded-full text-xl font-bold"
        >
          🔴 LIVE BATTLE
        </motion.span>
      </div>
    </motion.div>
  );
}

const VSCard = () => (
  <motion.div
    animate={{ scale: [1, 1.15, 1] }}
    transition={{ repeat: Infinity, duration: 1.2 }}
    className="flex items-center justify-center h-56 rounded-3xl
               bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600
               text-white text-7xl font-black
               shadow-[0_0_80px_rgba(255,165,0,0.9)]"
  >
    VS
  </motion.div>
);

function AdvancedFloorCard({
  floor,
  value,
  achievement,
  isWinner,
  isLoser,
  isDraw,
}) {
  const target = Number(floor.target);
  const remaining = Math.max(target - value, 0);

  const pct = Math.min(100, Math.round((value / target) * 100));
  const { color, text } = getProgressMeta(pct);

  /* STRIP (UNCHANGED) */
  const stripStyle = isWinner
    ? "bg-green-600"
    : isLoser
      ? "bg-red-600"
      : "bg-gray-700";

  const stripText = isWinner ? "🏆 WINNER" : isLoser ? "😞 LOSER" : "🤝 DRAW";

  return (
    <motion.div
      animate={{
        scale: isWinner ? [1, 1.05, 1] : 1,
        boxShadow: isWinner
          ? "0 0 50px rgba(34,197,94,0.7)"
          : "0 0 25px rgba(0,0,0,0.18)",
      }}
      transition={{ repeat: isWinner ? Infinity : 0, duration: 1.3 }}
      className="relative rounded-3xl p-10 border-4 bg-white shadow-2xl"
      style={{ borderColor: color }}
    >
      {/* WIN / LOSE / DRAW STRIP */}
      <div
        className={`absolute top-0 left-0 right-0 py-3
        text-center text-white font-black text-[32px]
        rounded-t-3xl ${stripStyle}`}
      >
        {stripText}
      </div>

      {/* IMAGE */}
      <div className="flex flex-col items-center mt-12">
        <img
          src={floor.photo}
          className="w-72 h-72 rounded-full border-4"
          style={{ borderColor: color }}
        />

        <p className="mt-4 text-[52px] font-black tracking-wide">
          {floor.name}
        </p>
      </div>

      {/* ORDERS */}
      <div className="mt-8 text-center">
        <div className="text-[96px] font-extrabold leading-none">
          <span style={{ color: color }}>{value}</span>
          <span className="opacity-40 text-[56px]"> / {target}</span>
        </div>

        <p className="mt-4 text-[34px] font-semibold text-gray-700">
          {remaining > 0
            ? `${remaining} orders to target`
            : "Target completed 🎉"}
        </p>
      </div>

      {/* PROGRESS */}
      <div className="mt-8">
        <div className="h-6 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.8 }}
            className="h-full rounded-full"
            style={{ backgroundColor: color }}
          />
        </div>

        <div className="mt-4 flex justify-between items-center">
          <p className="text-[36px] font-extrabold" style={{ color }}>
            {pct}% achieved
          </p>

          <p
            className="text-[40px] font-black tracking-wider"
            style={{ color }}
          >
            {text}
          </p>
        </div>
      </div>
    </motion.div>
  );
}